import { MarkerType, Position } from '@xyflow/react';
import dagre from 'dagre';

import { extractAddress } from '../../../utils/helperUtils';

/**
 * @typedef {import('./tracerStore').NodeTransactionSearchParams} NodeTransactionSearchParams
 * @typedef {import('./tracerStore').NodeTokenFlowSearchParams} NodeTokenFlowSearchParams
 * @typedef {import('./tracerStore').AddressNode} AddressNode
 * @typedef {import('./tracerStore').GroupNode} GroupNode
 * @typedef {import('./tracerStore').TextNode} TextNode
 * @typedef {import('./tracerStore').BtcTxnNode} BtcTxnNode
 * @typedef {import('./tracerStore').GenericEdge} GenericEdge
 * @typedef {import('./tracerStore').GroupEdge} GroupEdge
 * @typedef {import('./tracerStore').TextEdge} TextEdge
 * @typedef {import('blockscope/static/chainData').enrichedAddress} EnrichedAddress
 */

export const NODE_TYPE = {
  ADDRESS: 'addressNode',
  GROUP: 'addressGroup',
  BTC_TX: 'btcTxnNode',
  TEXT: 'textNode',
};
export const EDGE_TYPE = {
  GENERIC: 'genericEdge',
  GROUP: 'groupEdge',
  TEXT: 'textEdge',
};
export const NODE_WIDTH = 350;
export const NODE_HEIGHT = 70;

const DAGRE_CONFIG = {
  rankdir: 'LR',
  edgesep: 50,
  nodesep: 200,
  ranksep: 500,
};

export const NODE_STYLE = {
  DEFAULT: (selected) => {
    return {
      background: 'white',
      boxShadow: selected ? '0 0 15px blue' : 'none',
      borderWidth: selected ? 3 : 1,
    };
  },
  QUERY: (selected) => {
    return {
      background: '#FDCB6E',
      boxShadow: selected ? '0 0 15px blue' : 'none',
      borderWidth: selected ? 3 : 1,
    };
  },
  INTERESTED: (selected) => {
    return {
      background: '#29C5FF',
      boxShadow: selected ? '0 0 15px blue' : 'none',
      borderWidth: selected ? 3 : 1,
    };
  },
  GROUP: (selected) => {
    return {
      background: 'white',
      boxShadow: selected ? '0 0 15px blue' : 'none',
      borderWidth: selected ? 3 : 1,
    };
  },
};

export const EDGE_STYLE = {
  DEFAULT: {
    EDGE: {
      strokeWidth: 2,
      stroke: '#B2BEC3',
    },
    MARKER_END: {
      type: MarkerType.Arrow,
      strokeWidth: 1.5,
      width: 25,
      height: 25,
      color: '#B2BEC3',
    },
    LABEL_BG: { fill: '#455556B3', color: '#fff', fillOpacity: 0.7 },
    LABEL_STYLE: { fill: '#FFFFFF', fontWeight: 700 },
  },
  INFLOW: {
    EDGE: { strokeWidth: 2, stroke: '#0EAC51' },
    MARKER_END: {
      type: MarkerType.Arrow,
      strokeWidth: 1.5,
      width: 25,
      height: 25,
      color: '#0EAC51',
    },
    LABEL_BG: { fill: '#5EFCA1B3', color: '#fff', fillOpacity: 0.7 },
    LABEL_STYLE: { fill: '#000000', fontWeight: 700 },
  },
  OUTFLOW: {
    EDGE: { strokeWidth: 2, stroke: '#C0392B' },
    MARKER_END: {
      type: MarkerType.Arrow,
      strokeWidth: 1.5,
      width: 25,
      height: 25,
      color: '#C0392B',
    },
    LABEL_BG: { fill: '#F75C4CB3', color: '#fff', fillOpacity: 0.7 },
    LABEL_STYLE: { fill: '#FFFFFF', fontWeight: 700 },
  },
};

export const colorAttachedEdges = (edges, nodeId) => {
  const newEdgeArr = [];
  for (let edge of edges) {
    newEdgeArr.push(colorSingleEdge(edge, nodeId));
  }
  return newEdgeArr;
};

export const colorSingleEdge = (edge, nodeId) => {
  const toReturn = { ...edge };
  if (toReturn.type !== EDGE_TYPE.TEXT) {
    if (toReturn.target === nodeId) {
      toReturn.style = EDGE_STYLE.INFLOW.EDGE;
      toReturn.markerEnd = EDGE_STYLE.INFLOW.MARKER_END;
      toReturn.labelBgStyle = EDGE_STYLE.INFLOW.LABEL_BG;
      toReturn.labelStyle = EDGE_STYLE.INFLOW.LABEL_STYLE;
    } else if (toReturn.source === nodeId) {
      toReturn.style = EDGE_STYLE.OUTFLOW.EDGE;
      toReturn.markerEnd = EDGE_STYLE.OUTFLOW.MARKER_END;
      toReturn.labelBgStyle = EDGE_STYLE.OUTFLOW.LABEL_BG;
      toReturn.labelStyle = EDGE_STYLE.OUTFLOW.LABEL_STYLE;
    } else {
      toReturn.style = EDGE_STYLE.DEFAULT.EDGE;
      toReturn.markerEnd = EDGE_STYLE.DEFAULT.MARKER_END;
      toReturn.labelBgStyle = EDGE_STYLE.DEFAULT.LABEL_BG;
      toReturn.labelStyle = EDGE_STYLE.DEFAULT.LABEL_STYLE;
    }
  }
  return toReturn;
};

export const dagrify = (graphNodes, graphEdges, force = false) => {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph(DAGRE_CONFIG);

  graphNodes.forEach((n) => {
    dagreGraph.setNode(n.id, { ...n });
  });

  graphEdges.forEach((e) => {
    dagreGraph.setEdge(e.source, e.target);
  });

  dagre.layout(dagreGraph);

  let nodesToReturn = [];

  for (const node of graphNodes) {
    let dagreNode = dagreGraph.node(node.id);

    dagreNode.position =
      node.savedPosition && !force
        ? node.savedPosition
        : node.type === 'textNode'
        ? node.position
        : {
            x: dagreNode.x - (node.width || NODE_WIDTH) / 2,
            y: dagreNode.y - (node.height || NODE_HEIGHT) / 2,
          };

    nodesToReturn.push(dagreNode);
  }
  return { nodes: nodesToReturn, edges: graphEdges, graph: dagreGraph };
};

export const addressNodeId = ({ chain, address }) =>
  `${chain}:::${extractAddress(address)}`;

export const tokenFlowEdgeId = ({ source, target }) => `${source}-${target}`;

export const groupEdgeId = ({ source, target }) => `${source}-${target}`;

/**
 * Generate a query Key
 * @param {Object} QueryKeyParams
 * @param {Object | String } [QueryKeyParams.filterAddress]
 * @param {Object | String} QueryKeyParams.tokenAddress
 * @param {String} [QueryKeyParams.start]
 * @param {String} [QueryKeyParams.end]
 * @param {String} QueryKeyParams.chain
 * @param {String } [QueryKeyParams.transactionHash]
 */
export const newQueryId = ({
  filterAddress,
  tokenAddress,
  start,
  end,
  chain,
  transactionHash,
}) =>
  transactionHash
    ? `${chain}:::${transactionHash}:::${extractAddress(tokenAddress)}`
    : `${chain}:::${extractAddress(filterAddress)}:::${extractAddress(
        tokenAddress
      )}:::${start}:::${end}`;

/**
 * @param {Object} arg0
 * @param {EnrichedAddress} arg0.address
 * @param {(NodeTransactionSearchParams | NodeTokenFlowSearchParams)[]} [arg0.searchParams=[]]
 * @param {boolean} [arg0.interested = false]
 * @param {string} arg0.chain
 * @param {{x: number, y: number}} [savedPosition]
 * @returns {AddressNode}
 */
export const newGenericNode = (
  { address, searchParams = [], interested = false, chain },
  savedPosition
) => {
  return {
    id: addressNodeId({ chain, address: extractAddress(address) }),
    type: 'addressNode',
    position: savedPosition,
    savedPosition,
    data: {
      toolbarPosition: Position.Top,
      address,
      searchParams,
      interested,
      chain,
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  };
};

/**
 * @param {string} name
 * @param {AddressNode[]} members
 * @param {GenericEdge[]} internalEdges
 * @param {{x: number, y: number}} [savedPosition]
 * @returns {GroupNode}
 */
export const newGroupNode = (
  name,
  members = [],
  internalEdges = [],
  savedPosition
) => {
  return {
    id: `group:::${name}`,
    type: 'addressGroup',
    data: { name, members, internalEdges },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    savedPosition,
    position: savedPosition,
  };
};

/**
 * @param {string} hash
 * @param {{x: number, y: number}} [savedPosition]
 * @returns {BtcTxnNode}
 */
export const newBtcTxnNode = (hash, savedPosition) => {
  return {
    id: `BTC_TX:::${hash}`,
    type: 'btcTxnNode',
    data: { hash },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    savedPosition,
    position: savedPosition,
  };
};

/**
 *
 * @param {String} text
 * @param {{x: number, y: number}} position
 * @param {{width: number, height: number}} [size]
 * @param {string} [idOverride]
 * @returns {TextNode}
 */
export const newTextNode = (text, position, size, idOverride) => {
  const node = {
    id: idOverride || `text:::${Math.random().toString()}`,
    type: 'textNode',
    width: size?.width || 100,
    height: size?.height || 50,
    data: { text },
    position,
    savedPosition: position,
  };
  return node;
};

/**
 * @returns {GenericEdge}
 */
export const newGenericEdge = ({ source, target, queries }) => {
  return {
    id: tokenFlowEdgeId({ source, target }),
    source,
    target,
    labelBgPadding: [8, 4],
    labelBgBorderRadius: 4,
    animated: false,
    deletable: true,
    type: EDGE_TYPE.GENERIC,
    data: {
      source,
      target,
      queries,
    },
  };
};

/**
 * @returns {GroupEdge}
 */
export const newGroupEdge = ({ source, target, members = [] }) => {
  return {
    id: groupEdgeId({ source, target }),
    source,
    target,
    labelBgPadding: [8, 4],
    labelBgBorderRadius: 4,
    animated: false,
    deletable: true,
    type: EDGE_TYPE.GROUP,
    data: { source, target, members },
  };
};

/**
 * @returns {TextEdge}
 */
export const newTextEdge = ({ source, target, ...rest }) => {
  return {
    ...rest,
    id: rest?.edgeId || `${source}-${target}`,
    source,
    target,
    type: 'textEdge',
    data: {},
    deletable: true,
    style: {
      strokeWidth: 2,
      stroke: '#B2BEC3',
      strokeDasharray: ['5, 5'],
    },
  };
};
